/**
 * cbb-gallery.js
 * --------------------------------------------------------------------------
 * <cbb-product-gallery> — variant-aware Swiper gallery with one swiper per
 * color value (or a single swiper if the product has no color option).
 *
 * Reads color-option position from `data-color-index` and matches each
 * swiper's `data-color` against `variant.option{N}` from the variant JSON
 * dispatched by <cbb-variants>.
 *
 * Replaces the legacy product-gallery.js for CBB pages — do not load both together.
 *
 * Requires global `Swiper` to be available (Swiper 6+ recommended).
 * --------------------------------------------------------------------------
 */
(function () {
  if (customElements.get('cbb-product-gallery')) return;

  const SWIPER_OPTIONS = () => ({
    slidesPerView: 1,
    slidesPerGroup: 1,
    threshold: 10,
    loop: true,
    mousewheel: { forceToAxis: true }
  });

  class CbbProductGallery extends HTMLElement {
    constructor() {
      super();
      this._onVariantChanged = this._onVariantChanged.bind(this);
      this._instances = new WeakMap(); // swiperEl -> Swiper instance
    }

    connectedCallback() {
      this.swipers = this.querySelectorAll('.swiper');
      if (!this.swipers.length) return;

      const colorIndex = this.dataset.colorIndex;
      this.colorKey = colorIndex ? `option${colorIndex}` : null;

      // Init the swiper that's visible on first paint.
      const initial = this._activeSwiperEl();
      if (initial) this._initSwiper(initial);

      document.addEventListener('cbb:variant:changed', this._onVariantChanged);
    }

    disconnectedCallback() {
      document.removeEventListener('cbb:variant:changed', this._onVariantChanged);
    }

    _activeSwiperEl() {
      return [...this.swipers].find((el) => !el.classList.contains('hidden')) || this.swipers[0];
    }

    _initSwiper(el) {
      if (this._instances.has(el)) return this._instances.get(el);
      if (typeof window.Swiper !== 'function') {
        console.warn('[cbb-product-gallery] Swiper is not loaded.');
        return null;
      }

      const opts = Object.assign({}, SWIPER_OPTIONS(), {
        pagination: {
          el: el.querySelector('.swiper-pagination'),
          type: 'bullets',
          clickable: true
        },
        navigation: {
          nextEl: el.querySelector('.swiper-button-next'),
          prevEl: el.querySelector('.swiper-button-prev')
        }
      });

      const instance = new window.Swiper(el, opts);
      instance.on('beforeSlideChangeStart', () => {
        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      });

      this._instances.set(el, instance);
      return instance;
    }

    _onVariantChanged(event) {
      const variant = event.detail && event.detail.variant;
      if (!variant) return;

      // No color option — slide the single swiper to the variant's featured media.
      if (!this.colorKey) {
        this._slideToFeaturedMedia(variant);
        return;
      }

      const variantColor = variant[this.colorKey];
      if (variantColor == null) return;

      // Toggle visibility per swiper.
      let activeEl = null;
      this.swipers.forEach((swiper) => {
        const swiperColor = swiper.dataset.color;
        if (swiperColor === undefined) return; // not a per-color swiper
        const matches = swiperColor === variantColor;
        swiper.classList.toggle('hidden', !matches);
        if (matches) activeEl = swiper;
      });

      if (!activeEl) return;

      // Init lazily on first reveal, then reset to slide 0.
      const instance = this._initSwiper(activeEl) || this._instances.get(activeEl);
      if (instance) {
        // update() handles the case where Swiper was init'd while the parent
        // was display:none and computed its width as 0.
        instance.update();
        instance.slideTo(0, 0);
      }
    }

    _slideToFeaturedMedia(variant) {
      const featured = variant.featured_media;
      if (!featured || featured.id == null) return;

      const swiperEl = this._activeSwiperEl();
      if (!swiperEl) return;
      const instance = this._instances.get(swiperEl);
      if (!instance) return;

      // data-media-id is namespaced as "{section.id}-{media.id}". Match by suffix.
      const slides = swiperEl.querySelectorAll('.swiper-slide[data-media-id]');
      let targetIndex = -1;
      slides.forEach((slide, i) => {
        const id = slide.dataset.mediaId || '';
        if (id === String(featured.id) || id.endsWith('-' + featured.id)) {
          targetIndex = i;
        }
      });
      if (targetIndex < 0) return;

      // With loop:true, Swiper inserts duplicate slides at the start, so the
      // realIndex-aware `slideToLoop` is the right call.
      if (typeof instance.slideToLoop === 'function') {
        instance.slideToLoop(targetIndex, 0);
      } else {
        instance.slideTo(targetIndex, 0);
      }
    }
  }

  customElements.define('cbb-product-gallery', CbbProductGallery);
})();
